import { getAudioLevel, smoothAudioLevel } from './audioLevel'

export type MicrophoneMonitorStatus = 'idle' | 'connecting' | 'active' | 'unavailable'
interface Callbacks {
  onLevel: (level: number) => void
  onStatus: (status: MicrophoneMonitorStatus) => void
}

/** Owns only its visualisation stream. Never stops SpeechRecognition or sends/plays audio. */
export function startMicrophoneMonitor({ onLevel, onStatus }: Callbacks): () => void {
  let disposed = false
  let stream: MediaStream | undefined
  let context: AudioContext | undefined
  let source: MediaStreamAudioSourceNode | undefined
  let analyser: AnalyserNode | undefined
  let frame: number | undefined
  let previousTime: number | undefined
  let level = 0
  const tracks: MediaStreamTrack[] = []

  const stop = () => {
    if (disposed) return
    disposed = true
    if (frame !== undefined) window.cancelAnimationFrame(frame)
    for (const track of tracks) {
      track.removeEventListener('ended', unavailable)
      track.stop()
    }
    source?.disconnect()
    analyser?.disconnect()
    if (context && context.state !== 'closed') void context.close().catch(() => {})
  }

  const unavailable = () => {
    if (disposed) return
    stop()
    onLevel(0)
    onStatus('unavailable')
  }

  const AudioContextClass =
    window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!navigator.mediaDevices?.getUserMedia || !AudioContextClass) {
    onLevel(0)
    onStatus('unavailable')
    return stop
  }

  onLevel(0)
  onStatus('connecting')
  const start = async () => {
    try {
      const acquired = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      if (disposed) {
        acquired.getTracks().forEach((track) => track.stop())
        return
      }
      stream = acquired
      tracks.push(...stream.getTracks())
      if (!stream.getAudioTracks().some((track) => track.readyState === 'live')) {
        unavailable()
        return
      }
      tracks.forEach((track) => track.addEventListener('ended', unavailable))
      context = new AudioContextClass()
      analyser = context.createAnalyser()
      analyser.fftSize = 1024
      source = context.createMediaStreamSource(stream)
      source.connect(analyser) // Deliberately no connection to speakers.
      if (context.state === 'suspended') await context.resume()
      if (disposed) return
      if (context.state !== 'running') {
        unavailable()
        return
      }
      const samples = new Float32Array(analyser.fftSize)
      onStatus('active')
      const sample = (time: number) => {
        if (disposed || !analyser) return
        try {
          // Suspended/interrupted contexts can return the last captured buffer indefinitely.
          // Treat the gap as silence and resume naturally when the browser starts audio again.
          if (context?.state !== 'running') {
            level = 0
            previousTime = time
            onLevel(0)
            frame = window.requestAnimationFrame(sample)
            return
          }
          analyser.getFloatTimeDomainData(samples)
          const elapsed = previousTime === undefined ? 16 : Math.min(100, time - previousTime)
          previousTime = time
          level = smoothAudioLevel(level, getAudioLevel(samples), elapsed)
          onLevel(level < 0.005 ? 0 : level)
          frame = window.requestAnimationFrame(sample)
        } catch {
          unavailable()
        }
      }
      frame = window.requestAnimationFrame(sample)
    } catch {
      unavailable()
    }
  }
  void start()
  return stop
}
